import React from "react";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import {
  CustomTable,
  MobileTable,
  type CustomColumnDef,
  colors,
} from "@ippon/shared-components";
import { AREA_TYPE_LABELS, type Unit } from "@ippon/shared-types";

interface Props {
  units: Unit[];
  loading?: boolean;
  onRowClick?: (unit: Unit) => void;
  /** 一括削除用: チェックボックス列を表示する */
  enableCheckboxSelection?: boolean;
  /** 親が制御する場合の選択 ID 配列 */
  selectedIds?: string[];
  /** 選択が変わった時のコールバック */
  onRowSelection?: (ids: string[]) => void;
  /** テーブル内ページングの表示件数 */
  pageSize?: number;
}

/** ISO8601 → "YYYY/MM/DD HH:mm" (空なら "-") */
const formatDateTime = (iso?: string): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}/${mo}/${da} ${h}:${mi}`;
};

/**
 * 物件構成タブの「区画」一覧テーブル。
 * カラム順: 区分 → 名称 → 収益 → 更新日時。
 * 名称はリンク (常時下線、hover で青) として描画する。
 */
const UnitListTable: React.FC<Props> = ({
  units,
  loading,
  onRowClick,
  enableCheckboxSelection,
  selectedIds,
  onRowSelection,
  pageSize,
}) => {
  const columns: CustomColumnDef[] = [
    {
      field: "area_type",
      headerName: "場所区分",
      flex: 1,
      valueGetter: ({ row }: { row: Unit }) => AREA_TYPE_LABELS[row.area_type],
    },
    {
      field: "landmark_name",
      headerName: "名称",
      flex: 2,
      sortable: true,
      tooltipValueGetter: ({ row }) => String((row as Unit).landmark_name || ""),
      renderCell: ({ row }) => (
        <Typography
          component="a"
          onClick={() => onRowClick?.(row as Unit)}
          sx={{
            fontSize: 14,
            color: colors.text.primary,
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: colors.primary.main,
            },
          }}
        >
          {(row as Unit).landmark_name}
        </Typography>
      ),
    },
    {
      field: "revenue_type",
      headerName: "収益カテゴリ",
      flex: 1,
      valueGetter: ({ row }: { row: Unit }) => {
        if (row.revenue_type === "stock_rent") return "貸室";
        if (row.revenue_type === "stock_other") return "付帯";
        if (row.revenue_type === "none") return "収益なし";
        return "-"; // 値が空 (未設定) のときのみハイフン
      },
    },
    {
      field: "updated_at",
      headerName: "更新日時",
      flex: 1.2,
      sortable: true,
      valueGetter: ({ row }: { row: Unit }) => formatDateTime(row.updated_at),
    },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const rows = units.map((u) => ({ ...u, id: u.unit_id }));

  // MobileTable は本文で valueGetter 非対応 (renderCell のみ)。デスクトップ列は
  // valueGetter を使うため、モバイル用に renderCell 付き列を別途生成する。
  // 名称(landmark_name)は renderTitle で出すので本文からは除外。
  const mobileColumns: CustomColumnDef[] = columns
    .filter((c) => c.field !== "landmark_name")
    .map((col) => {
      if (col.field === "area_type")
        return {
          ...col,
          renderCell: ({ row }: { row: Unit }) =>
            AREA_TYPE_LABELS[row.area_type],
        };
      if (col.field === "revenue_type")
        return {
          ...col,
          renderCell: ({ row }: { row: Unit }) => {
            if (row.revenue_type === "stock_rent") return "貸室";
            if (row.revenue_type === "stock_other") return "付帯";
            if (row.revenue_type === "none") return "収益なし";
            return "-";
          },
        };
      if (col.field === "updated_at")
        return {
          ...col,
          renderCell: ({ row }: { row: Unit }) =>
            formatDateTime(row.updated_at),
        };
      return col;
    });

  if (isMobile) {
    return (
      <MobileTable
        rows={rows}
        columns={mobileColumns}
        checkBoxOpen={!!enableCheckboxSelection}
        selectedIds={selectedIds}
        onRowSelection={onRowSelection}
        pageSize={pageSize}
        isLoading={loading}
        noDataMessage="区画が登録されていません"
        noDataIcon={
          <MeetingRoomOutlinedIcon
            sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
          />
        }
        handlePropertyClick={(row) => onRowClick?.(row as Unit)}
        renderTitle={(row) => (
          <Typography
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onRowClick?.(row as Unit);
            }}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: colors.text.primary,
              textDecoration: "underline",
              cursor: "pointer",
              "&:hover": { color: colors.primary.main },
            }}
          >
            {(row as Unit).landmark_name}
          </Typography>
        )}
      />
    );
  }

  return (
    <CustomTable
      rows={rows}
      columns={columns}
      isLoading={loading}
      enableCheckboxSelection={enableCheckboxSelection}
      selectedIds={selectedIds}
      onRowSelection={onRowSelection}
      pageSize={pageSize}
      minHeight="auto"
      noDataMessage="区画が登録されていません"
      noDataIcon={
        <MeetingRoomOutlinedIcon
          sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
        />
      }
    />
  );
};

export default UnitListTable;
